<template>
  <v-card>
    <v-slide-y-transition mode="out-in">
      <v-card-text>
        <slot name="header"></slot>
        <div ref="chart" style="width:100%; height:540px;"></div>
      </v-card-text>
    </v-slide-y-transition>
  </v-card>
</template>

<script>
  import colors from 'vuetify/es5/util/colors'
  import Highcharts from '../../libs/highcharts'
  import { getProp, hexToRGBA, log10, debounce } from '../../libs/utils.js'
  import UserService from '../../services/user.service.js'
  import MongoService from '../../services/mongo.service'
  import * as CloudWatch from '../../services/cloudwatch.service'
  import moment from 'moment'
  import { zipObjectDeep, sortBy } from 'lodash'

  export default {
    props: {
      // data: { type: Array, required: true },
      accountId: { type: String },
      type: { type: String },
      range: { type: Number }
    },
    data () {
      return {
        loading: false,
        botConfig: null,
        data: [],
        chart: null,
        chartData: {},
        chartFilter: {
          from: null,
          to: null
        },
        chartLoaderId: 0
      }
    },
    async beforeMount () {
      this.loading = true
      // try {} catch (e) {}
      this.loading = false
    },
    computed: {
      sortedData () {
        return [...this.data]
          .map(row => {
            row['created_at'] = +new Date(row['created_at'])
            return row
          })
          .sort((a, b) => a['created_at'] - b['created_at'])
      },
      sortedResultData () {
        return this.sortedData
          .filter(row => row['result'])
      }
    },
    watch: {
      'accountId' () {
        this.$nextTick(() => this.initPage())
      },
      'type' () {
        this.$nextTick(() => this.initPage())
      },
      'range' () {
        this.$nextTick(() => this.initPage())
      },
      'data' (val) {
        this.$nextTick(() => this.refreshChartDataUI())
      }
    },
    async mounted () {
      this.initPage()
    },
    methods: {
      initPage () {
        this.chartFilter.from = +moment.utc().add(-30, 'minutes').startOf('day').toDate()
        this.chartFilter.to = +moment.utc().endOf('day').toDate()
        this.initChart()
        this.$nextTick(() => this.refreshChart())
      },
      initChart () {
        const { chart } = this.$refs
        const self = this
        self.chart && self.chart.destroy()
        self.chart = Highcharts.stockChart(chart, {
          title: {
            text: 'Bot Monitoring'
          },
          legend: {
            enabled: true
          },
          plotOptions: {
            series: {
              dataGrouping: {
                enabled: true
              },
              stacking: 'normal',
              allowPointSelect: true,
              events: {
                async click (e) {
                  const loaderId = self.showLoading()
                  const row = self.getRowFromChartPointData(e.point)
                  const item = getProp(row, '_item')
                  if (item !== undefined) {
                    const scope = getProp(row, '_scope')
                    const index = self.data.indexOf(item)
                    let from = 0
                    let to = 0
                    if (e.point.series.currentDataGrouping) {
                      const intervalRange = e.point.series.currentDataGrouping.totalRange - 1
                      from = e.point.x
                      to = from + intervalRange
                    } else {
                      const itemGroupFrom = +new Date(getProp(item, '_group_range.from', item['created_at']))
                      const itemGroupTo = +new Date(getProp(item, '_group_range.to', item['created_at']))
                      from = Math.min(itemGroupFrom, item['created_at'])
                      to = Math.max(itemGroupTo, item['created_at'])
                    }

                    if (item.error) {
                      const groupedData = [item]
                      const requestId = (/.*RequestId: (.*) Process exited.*/.exec(item.error) || [])[1]
                      const hasPermission = UserService.hasPermission('cloudwatch', 'search')
                      const errorMessage = requestId && hasPermission
                        ? (await CloudWatch.search({
                          requestId,
                          startTime: +moment(item['updated_at'] || item['created_at']).add(-1, 'hours'),
                          endTime: +moment(item['updated_at'] || item['created_at']).add(1, 'hours')
                        })).message
                        : item.error
                      self.$emit('click', { error: { requestId: requestId || '', message: errorMessage }, item, scope, index, groupedData, from, to })
                    } else {
                      let groupedMonitoringData = await MongoService.find({
                        'collection': 'historical-executions-monitoring-charts-weekly',
                        'filter': { 'type': `${self.type}`, 'account_id': self.accountId, 'created_at': { '$gte': from, '$lte': to } },
                        'sort': { 'created_at': 1 },
                        'limit': 2000,
                        'projection': { 'created_at': 1, 'execution_id': 1 }
                      })
                      groupedMonitoringData = sortBy(groupedMonitoringData, ['created_at'])
                      const minMonitoringData = groupedMonitoringData[0]
                      const maxMonitoringData = groupedMonitoringData.slice(-1)[0]
                      const [minExecutionData, maxExecutionData] = await Promise.all([
                        MongoService.find({
                          'collection': 'historical-executions',
                          'filter': { '_id': minMonitoringData['execution_id'] },
                          'projection': { 'created_at': 1 },
                          'limit': 1
                        }).then(items => items[0] || {}),
                        MongoService.find({
                          'collection': 'historical-executions',
                          'filter': { '_id': maxMonitoringData['execution_id'] },
                          'projection': { 'created_at': 1 },
                          'limit': 1
                        }).then(items => items[0] || {})
                      ])
                      from = +new Date(minExecutionData['created_at'])
                      to = +new Date(maxExecutionData['created_at'])
                      console.log({ from, to, minMonitoringData, maxMonitoringData, minExecutionData, maxExecutionData })
                      const groupedData = await MongoService.find({
                        'collection': 'historical-executions',
                        'filter': { 'type': `bot-${self.type}`, 'account_id': self.accountId, 'created_at': { '$gte': from, '$lte': to } },
                        'sort': { 'created_at': 1 },
                        'limit': 2000
                      })
                      self.$emit('click', { item, scope, index, groupedData, from, to })
                    }
                  }
                  self.hideLoading(loaderId)
                }
              }
            },
            spline: {
              dataLabels: {
                enabled: false,
                y: 25,
                x: 0
              },
              marker: {
                enabled: false
              }
            },
            line: {
              marker: {
                enabled: true,
                radius: 3,
                symbol: 'circle'
              },
              lineWidth: 0,
              states: {
                hover: {
                  enabled: true,
                  lineWidthPlus: 0
                }
              }
            }
          },
          navigator: {
            baseSeries: 2,
            adaptToUpdatedData: false
          },
          // rangeSelector: {
          //   selected: null
          // },
          xAxis: {
            // maxRange: 4 * 3600 * 1000,
            // min: self.chartFilter.from,
            // max: self.chartFilter.to,
            events: {
              afterSetExtremes (e) {
                if (!e.min || !e.max) return
                if (self.chartFilter.from !== e.min || self.chartFilter.to !== e.max) {
                  self.chartFilter.from = e.min
                  self.chartFilter.to = e.max
                  console.log(new Date(self.chartFilter.from).toISOString(), new Date(self.chartFilter.to).toISOString())
                  self.$emit('change', e)
                  // console.log('afterSetExtremes', self.chartFilter.from, self.chartFilter.to)
                  self.refreshChartData()
                }
                return e
              }
            }
          },
          yAxis: [{
            title: {
              text: 'Value'
            },
            height: '70%',
            resize: {
              enabled: true
            }
          }, {
            title: {
              text: `Balances<br/><span style="fill:${colors.grey.lighten1}">(Average)</span>`
            },
            labels: {
              enabled: false
            },
            min: null,
            top: '75%',
            height: '25%',
            offset: 0
          }],
          series: [{
            name: 'Bid',
            type: 'columnrange',
            color: hexToRGBA(colors.green.lighten4, 0.5)
          }, {
            name: 'Ask',
            type: 'columnrange',
            color: hexToRGBA(colors.red.lighten4, 0.5)
          }, {
            name: 'Mid',
            type: 'spline',
            color: Highcharts.theme.colors[0],
            tooltip: {
              pointFormatter () {
                const { series, color, y } = this
                const row = self.getRowFromChartPointData(this)
                const item = getProp(row, '_item', {})
                if (item['error'] && row['y'] === 0) {
                  return ''
                }
                return `<span style="color:${color}">●</span> ${series.name}: <b>${y}</b><br/>`
              }
            }
          }, {
            name: 'Buy',
            type: 'line',
            color: hexToRGBA(colors.green.darken1, 0.75),
            stacking: null,
            dataLabels: {
              zIndex: 10
            }
          }, {
            name: 'Sell',
            type: 'line',
            color: hexToRGBA(colors.red.darken1, 0.75),
            stacking: null,
            dataLabels: {
              zIndex: 10
            }
          }, {
            name: 'Order Buy Cancel',
            type: 'line',
            color: hexToRGBA(colors.green.darken4, 0.8),
            stacking: null,
            marker: {
              symbol: 'cross',
              lineColor: null,
              lineWidth: 3
            }
          }, {
            name: 'Order Buy Open',
            type: 'line',
            color: hexToRGBA(colors.green.darken4, 0.8),
            stacking: null,
            marker: {
              symbol: 'diamond',
              radius: 4
            }
            // tooltip: {
            //   pointFormatter () {
            //     const { series, color, y, index } = this
            //     const item = getProp(self.chartData.orderBuys[index], '_item', {})
            //     console.log(this, item)
            //     return `<span style="color:${color}">●</span> ${series.name}: <b>${y}</b><br/>`
            //   }
            // }
          }, {
            name: 'Order Sell Cancel',
            type: 'line',
            color: hexToRGBA(colors.red.darken4, 0.8),
            stacking: null,
            marker: {
              symbol: 'cross',
              lineColor: null,
              lineWidth: 3
            }
          }, {
            name: 'Order Sell Open',
            type: 'line',
            color: hexToRGBA(colors.red.darken4, 0.8),
            stacking: null,
            marker: {
              symbol: 'diamond',
              radius: 4
            }
          }, {
            yAxis: 1,
            name: 'Balance (Quote)',
            type: 'column',
            color: hexToRGBA(colors.red.lighten1, 0.6),
            stacking: null,
            dataGrouping: {
              approximation: 'average'
            },
            tooltip: {
              valueDecimals: 2,
              pointFormatter () {
                const { series, color } = this
                const row = self.getRowFromChartPointData(this)
                const y = getProp(row, 'y_original', 0)
                const currency = getProp(row, 'currency', '')
                return `<span style="color:${color}">●</span> ${series.name}: <b>${Highcharts.numberFormat(y, 2, '.', ',')}</b> ${currency}<br/>`
              }
            }
          }, {
            yAxis: 1,
            name: 'Balance (Base)',
            type: 'column',
            color: hexToRGBA(colors.green.lighten1, 0.6),
            stacking: null,
            dataGrouping: {
              approximation: 'average'
            },
            tooltip: {
              valueDecimals: 2,
              pointFormatter () {
                const { series, color } = this
                const row = self.getRowFromChartPointData(this)
                const y = getProp(row, 'y_original', 0)
                const currency = getProp(row, 'currency', '')
                return `<span style="color:${color}">●</span> ${series.name}: <b>${Highcharts.numberFormat(y, 2, '.', ',')}</b> ${currency}<br/>`
              }
            }
          }, {
            yAxis: 1,
            name: 'Balance Min (Quote)',
            type: 'spline',
            color: hexToRGBA(colors.red.darken4, 0.8),
            stacking: null,
            dataGrouping: {
              approximation: 'close'
            },
            tooltip: {
              valueDecimals: 2,
              pointFormatter () {
                const { series, color } = this
                const row = self.getRowFromChartPointData(this)
                const y = getProp(row, 'y_original', 0)
                const currency = getProp(row, 'currency', '')
                return `<span style="color:${color}">●</span> ${series.name}: <b>${Highcharts.numberFormat(y, 2, '.', ',')}</b> ${currency}<br/>`
              }
            }
          }, /* {
            yAxis: 1,
            name: 'Balance Max (Quote)',
            type: 'spline',
            color: hexToRGBA(colors.red.darken3, 0.75),
            stacking: null,
            dataGrouping: {
              approximation: 'open'
            },
            dashStyle: 'ShortDot',
            lineWidth: 1,
            marker: {
              enabled: false,
              radius: 2,
              symbol: 'circle'
            },
            tooltip: {
              pointFormatter () {
                const { series, color } = this
                const row = self.getRowFromChartPointData(this)
                const y = getProp(row, 'y_original', 0)
                const currency = getProp(row, 'currency', '')
                return `<span style="color:${color}">●</span> ${series.name}: <b>${Highcharts.numberFormat(y, 2, '.', ',')}</b> ${currency}<br/>`
              }
            }
          } , */ {
            yAxis: 1,
            name: 'Balance Min (Base)',
            type: 'spline',
            color: hexToRGBA(colors.green.darken4, 0.8),
            stacking: null,
            dataGrouping: {
              approximation: 'close'
            },
            tooltip: {
              valueDecimals: 2,
              pointFormatter () {
                const { series, color } = this
                const row = self.getRowFromChartPointData(this)
                const y = getProp(row, 'y_original', 0)
                const currency = getProp(row, 'currency', '')
                return `<span style="color:${color}">●</span> ${series.name}: <b>${Highcharts.numberFormat(y, 2, '.', ',')}</b> ${currency}<br/>`
              }
            }
          }, /* {
            yAxis: 1,
            name: 'Balance Max (Base)',
            type: 'spline',
            color: hexToRGBA(colors.green.darken3, 0.75),
            stacking: null,
            dataGrouping: {
              approximation: 'open'
            },
            dashStyle: 'ShortDot',
            lineWidth: 1,
            marker: {
              enabled: false,
              radius: 2,
              symbol: 'circle'
            },
            tooltip: {
              pointFormatter () {
                const { series, color } = this
                const row = self.getRowFromChartPointData(this)
                const y = getProp(row, 'y_original', 0)
                const currency = getProp(row, 'currency', '')
                return `<span style="color:${color}">●</span> ${series.name}: <b>${Highcharts.numberFormat(y, 2, '.', ',')}</b> ${currency}<br/>`
              }
            }
          }, */ {
            yAxis: 1,
            name: 'Error',
            type: 'line',
            color: hexToRGBA(colors.shades.black, 0.8),
            stacking: null,
            // showInNavigator: true,
            dataGrouping: {
              approximation: 'open'
            },
            marker: {
              radius: 1
            },
            tooltip: {
              pointFormatter () {
                const { series, color } = this
                const row = self.getRowFromChartPointData(this)
                const item = getProp(row, '_item', {})
                return `<span style="color:${color}">●</span> ${series.name}: <b>${item.error}</b><br/>`
              }
            }
          }]
        })
      },
      refreshChartTimeRange: debounce(async function () {
        const loaderId = this.showLoading()
        const from = +moment.utc().add(-this.range, 'day').startOf('day').toDate()
        const to = +moment.utc().endOf('day').toDate()
        const items = await this.getChartData(
          from,
          to,
          this.accountId,
          this.type,
          {'error': 1, 'result.mid': 1}
        )
        // if (!this.chart.navigator.series[0].xData.length) {
        const points = items
          .map(item => ({
            x: +new Date(item['created_at']),
            y: getProp(item, 'result.mid', 0)
          }))
          // .concat([{
          //   x: +new Date(),
          //   y: null
          // }])
        this.chart.series[this.chart.series.length - 1].setData(points)
        // }
        setTimeout(() => {
          this.hideLoading(loaderId)
        }, 1000)
      }, 1000),
      refreshChartData: debounce(async function () {
        const loaderId = this.showLoading()
        try {
          this.data = await this.getChartData(
            this.chartFilter.from,
            this.chartFilter.to,
            this.accountId,
            this.type
          )
        } catch (e) {}
        setTimeout(() => this.hideLoading(loaderId), 1000)
      }, 500),
      // tslint:disable-next-line
      async getChartData (from, to, accountId, type, project = undefined, group = undefined) {
        if (!accountId || !type) return []
        const response = await MongoService.monitoring.botGraph({
          from,
          to,
          account_id: accountId,
          // type, // Deprecated parameter
          project,
          group
        })
        if (!this.botConfig) {
          this.botConfig = (await MongoService.find({
            'collection': 'bot-configs',
            'filter': { type, 'account_id': accountId },
            'limit': 1
          }))[0]
        }
        let data = getProp(response, 'values', []).map(row => zipObjectDeep(response.keys || [], row))
        data = data
          .map(row => {
            row['created_at'] = +new Date(row['created_at'])
            return row
          })
          .sort((a, b) => a['created_at'] - b['created_at'])
        return data
        // return mapObjectByArrayKeysValues(data['keys'], data['values'])
      },
      getBalanceChartPointValue (val) {
        return log10(val, 0)
      },
      refreshChartDataUI () {
        this.chartData = {}
        let serieIndex = 0
        const sortedData = this.sortedData
        const sortedResultData = this.sortedResultData
        const quoteCurrency = getProp(this.botConfig, 'quote_currency')
        const quoteAccountingCurrency = getProp(this.botConfig, 'accounting_currency')
        const baseCurrency = getProp(this.botConfig, 'base_currency')
        const baseAccountingCurrency = quoteAccountingCurrency

        this.chartData.bids = sortedResultData
          .filter(row => getProp(row, 'result.windows[0]', null) !== null)
          .map(row => ({ _item: row, _scope: 'windows', x: row['created_at'], low: getProp(row, 'result.windows[0]', 0), high: getProp(row, 'result.windows[1]', 0) }))
        this.chart.series[serieIndex++].setData(this.chartData.bids)

        this.chartData.asks = sortedResultData
          .filter(row => getProp(row, 'result.windows[2]', null) !== null)
          .map(row => ({ _item: row, _scope: 'windows', x: row['created_at'], low: getProp(row, 'result.windows[2]', 0), high: getProp(row, 'result.windows[3]', 0) }))
        this.chart.series[serieIndex++].setData(this.chartData.asks)

        this.chartData.mids = sortedResultData
          .filter(row => getProp(row, 'result.mid', null) !== null)
          .map(row => ({ _item: row, _scope: 'mid', x: row['created_at'], y: getProp(row, 'result.mid', 0) }))
          // .concat([{
          //   x: +new Date(),
          //   y: null
          // }])
        this.chart.series[serieIndex++].setData(this.chartData.mids)

        this.chartData.buys = sortedResultData
          .reduce((arr, row) => {
            const items = (getProp(row, 'result.orders') || []).filter(r => r['side'] === 'buy' && r['status'] === 'opened')
            items.forEach(item => arr.push({ _item: row, _scope: 'open_orders', x: row['created_at'], y: item['price'] }))
            return arr
          }, [])
        this.chart.series[serieIndex++].setData(this.chartData.buys)

        this.chartData.sells = sortedResultData
          .reduce((arr, row) => {
            const items = (getProp(row, 'result.orders') || []).filter(r => r['side'] === 'sell' && r['status'] === 'opened')
            items.forEach(item => arr.push({ _item: row, _scope: 'open_orders', x: row['created_at'], y: item['price'] }))
            return arr
          }, [])
        this.chart.series[serieIndex++].setData(this.chartData.sells)

        this.chartData.orderBuysCancel = sortedResultData
          .reduce((arr, row) => {
            const items = (getProp(row, 'result.orders') || []).filter(r => r['side'] === 'buy' && r['status'] === 'canceled')
            if (!items.length) {
              arr.push({ _item: row, _scope: 'order_canceled', x: row['created_at'], y: null })
            } else {
              items.forEach(item => {
                arr.push({ _item: row, _scope: 'order_canceled', x: row['created_at'], y: item['price'] })
              })
            }
            return arr
          }, [])
        this.chart.series[serieIndex++].setData(this.chartData.orderBuysCancel)

        this.chartData.orderBuys = sortedResultData
          .reduce((arr, row) => {
            const items = (getProp(row, 'result.orders') || []).filter(r => r['side'] === 'buy' && r['status'] === 'created')
            if (!items.length) {
              arr.push({ _item: row, _scope: 'order_created', x: row['created_at'], y: null })
            } else {
              items.forEach(item => {
                arr.push({ _item: row, _scope: 'order_created', x: row['created_at'], y: item['price'] })
              })
            }
            return arr
          }, [])
        this.chart.series[serieIndex++].setData(this.chartData.orderBuys)

        this.chartData.orderSellsCancel = sortedResultData
          .reduce((arr, row) => {
            const items = (getProp(row, 'result.orders') || []).filter(r => r['side'] === 'sell' && r['status'] === 'canceled')
            if (!items.length) {
              arr.push({ _item: row, _scope: 'order_canceled', x: row['created_at'], y: null })
            } else {
              items.forEach(item => {
                arr.push({ _item: row, _scope: 'order_canceled', x: row['created_at'], y: item['price'] })
              })
            }
            return arr
          }, [])
        this.chart.series[serieIndex++].setData(this.chartData.orderSellsCancel)

        this.chartData.orderSells = sortedResultData
          .reduce((arr, row) => {
            const items = (getProp(row, 'result.orders') || []).filter(r => r['side'] === 'sell' && r['status'] === 'created')
            if (!items.length) {
              arr.push({ _item: row, _scope: 'order_created', x: row['created_at'], y: null })
            } else {
              items.forEach(item => {
                arr.push({ _item: row, _scope: 'order_created', x: row['created_at'], y: item['price'] })
              })
            }
            return arr
          }, [])
        this.chart.series[serieIndex++].setData(this.chartData.orderSells)

        this.chartData.balancesQuote = sortedResultData
          .reduce((arr, row) => {
            const y = getProp(row, 'result.balances[1]', null)
            if (y !== null) {
              arr.push({ _item: row, _scope: 'configuration', x: row['created_at'], y: this.getBalanceChartPointValue(y), y_original: y, currency: quoteAccountingCurrency })
            }
            return arr
          }, [])
        this.chart.series[serieIndex].name = `Balance (${quoteCurrency})`
        // this.chart.series[serieIndex].tooltipOptions.valueSuffix = ` ${quoteAccountingCurrency}`
        this.chart.series[serieIndex++].setData(quoteCurrency ? this.chartData.balancesQuote : [])

        this.chartData.balancesBase = sortedResultData
          .reduce((arr, row) => {
            const y = getProp(row, 'result.balances[0]', null)
            if (y !== null) {
              arr.push({ _item: row, _scope: 'configuration', x: row['created_at'], y: this.getBalanceChartPointValue(y), y_original: y, currency: baseAccountingCurrency })
            }
            return arr
          }, [])
        this.chart.series[serieIndex].name = `Balance (${baseCurrency})`
        // this.chart.series[serieIndex].tooltipOptions.valueSuffix = ` ${baseAccountingCurrency}`
        this.chart.series[serieIndex++].setData(baseCurrency ? this.chartData.balancesBase : [])

        // const maxBalanceValue = Math.max.apply(
        //   null,
        //   [ ...this.chartData.balancesQuote.map(row => row['y']), ...this.chartData.balancesBase.map(row => row['y']) ]
        // )
        const balanceItems = [
          { name: 'balancesQuoteMin', label: `Balance Min (${quoteCurrency})`, labelSuffix: ` ${quoteAccountingCurrency}`, propPath: 'result.balances[4]' },
          { name: 'balancesBaseMin', label: `Balance Min (${baseCurrency})`, labelSuffix: ` ${baseAccountingCurrency}`, propPath: 'result.balances[2]' }
        ]
        balanceItems.forEach(config => {
          this.chartData[config['name']] = sortedResultData
            .reduce((arr, row) => {
              const value = getProp(row, config['propPath'], null)
              if (value !== null) {
                arr.push({ _item: row, _scope: 'configuration', x: row['created_at'], y: this.getBalanceChartPointValue(value), y_original: value, currency: config['labelSuffix'] })
              }
              return arr
            }, [])
          this.chart.series[serieIndex].name = config['label']
          // this.chart.series[serieIndex].tooltipOptions.valueSuffix = config['labelSuffix']
          this.chart.series[serieIndex++].setData(this.chartData[config['name']])
        })

        this.chartData.errors = sortedData
          .filter(row => getProp(row, 'error'))
          .map(row => {
            return { _item: row, x: row['created_at'], y: 0 }
          })
        this.chart.series[serieIndex++].setData(this.chartData.errors)

        // const minNavValue = min(this.chartData.mids.map(item => item['y'])) || 0
        // this.chartData.navigator = sortedData
        //   .map(row => {
        //     const value = row['error'] ? minNavValue : getProp(row, 'result.mid.mid', minNavValue)
        //     return { _item: row, x: row['created_at'], y: value }
        //   })
        // this.chart.series[serieIndex++].setData(this.chartData.navigator)
      },
      getRowFromChartPointData (point) {
        const { series, x } = point
        const data = series.options.data
        const row = data.find(item => item.x >= x) || {}
        return row
      },
      showLoading () {
        this.chart.showLoading('Loading data from server...')
        this.chartLoaderId = Date.now()
        return this.chartLoaderId
      },
      hideLoading (id) {
        if (this.chartLoaderId === id) {
          this.chart.hideLoading()
        }
      },
      async refreshChart () {
        await this.refreshChartTimeRange()
        await this.refreshChartData()
      },
      async refreshChartToLatest () {
        const { min, max } = this.chart.xAxis[0].getExtremes()
        const diff = Date.now() - max
        await this.refreshChart()
        this.$nextTick(() => {
          this.chart.xAxis[0].setExtremes(min + diff, max + diff, true)
        })
      }
    }
  }
</script>
